import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ArrowRight, Upload, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import { Calendar } from "@/components/ui/calendar";

const BUDGETS = ["Under $500", "$500–$1,000", "$1,000–$2,500", "$2,500–$5,000", "$5,000–$10,000", "$10,000+", "Not sure / Need an estimate"];
const TIME_WINDOWS = ["Morning (8am–12pm)", "Afternoon (12pm–4pm)", "Evening (4pm–7pm)", "Flexible"];
const STEPS = ["Service", "Location", "Photos & Budget", "Estimate & Schedule", "Contact", "Review"];

const inputCls = "w-full h-14 border border-border px-4 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flame focus:border-transparent";

const Radio = ({ options, value, onChange, testPrefix, render }) => (
  <div className="grid gap-2">
    {options.map((o) => {
      const val = typeof o === "string" ? o : o.value;
      const label = typeof o === "string" ? o : o.label;
      return (
        <button key={val} type="button" data-testid={`${testPrefix}-${val.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => onChange(val)}
          className={`text-left border px-5 py-4 text-sm font-medium transition-colors ${
            value === val ? "border-flame bg-orange-50 text-navy ring-1 ring-flame" : "border-border bg-white text-slate-600 hover:border-slate-300"
          }`}>
          {render ? render(o) : label}
        </button>
      );
    })}
  </div>
);

export default function RequestWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({
    category_slug: "", is_emergency: null, city: "", zip_code: "", address: "", description: "",
    budget_range: "", estimate_method: "", preferred_date: null, preferred_time_window: "",
    contact_preference: "", contact_name: "", contact_phone: "", contact_email: "",
  });

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setF((p) => ({ ...p, contact_name: p.contact_name || user.name, contact_email: p.contact_email || user.email }));
  }, [user]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const categoryName = useMemo(() => categories.find((c) => c.slug === f.category_slug)?.name || "", [categories, f.category_slug]);

  const canNext = [
    !!f.category_slug && f.is_emergency !== null,
    f.city && f.zip_code.length === 5 && f.description.trim().length >= 10,
    !!f.budget_range,
    f.estimate_method && (f.is_emergency || f.estimate_method !== "onsite" || f.preferred_date),
    f.contact_preference && f.contact_name && f.contact_phone.length >= 7 && /\S+@\S+\.\S+/.test(f.contact_email),
    true,
  ][step];

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads", fd);
        setPhotos((p) => [...p, { id: data.id, name: file.name }]);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/requests", {
        ...f,
        address: f.address.trim() || null,
        preferred_date: f.preferred_date ? format(f.preferred_date, "yyyy-MM-dd") : null,
        photo_ids: photos.map((p) => p.id),
      });
      // Lead is confirmed saved by the API before we show success (Google Ads conversion URL).
      navigate("/request/success", { state: { emergency: f.is_emergency } });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit request. Please try again — your request was NOT saved.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <AppHeader />
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-2">
          <p className="uppercase tracking-[0.25em] text-flame font-heading font-bold text-xs">Step {step + 1} of {STEPS.length}</p>
          <p className="text-xs text-slate-400">{STEPS[step]}</p>
        </div>
        <div className="h-1 bg-border mb-8">
          <div className="h-1 bg-flame transition-[width] duration-300" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>

        {f.is_emergency && (
          <div data-testid="emergency-banner" className="flex items-center gap-3 bg-red-600 text-white px-5 py-3 mb-6 font-heading font-bold uppercase tracking-wider text-sm">
            <AlertTriangle size={18} /> Urgent / Emergency Request
          </div>
        )}

        <div className="bg-white border border-border p-8" data-testid={`wizard-step-${step}`}>
          {step === 0 && (
            <div>
              <h2 className="font-heading font-black tracking-tight text-navy text-2xl mb-6">What service do you need?</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {categories.map((c) => (
                  <button key={c.id} type="button" data-testid={`wizard-category-${c.slug}`}
                    onClick={() => set("category_slug", c.slug)}
                    className={`border px-3 py-4 text-sm font-medium transition-colors ${
                      f.category_slug === c.slug ? "border-flame bg-orange-50 text-navy ring-1 ring-flame" : "border-border text-slate-600 hover:border-slate-300"
                    }`}>
                    {c.name}
                  </button>
                ))}
              </div>
              <div className="mt-8 border-t border-border pt-6">
                <p className="text-sm font-medium text-navy mb-3">Is this an emergency? <span className="text-flame">*</span></p>
                <div className="grid grid-cols-2 gap-3" data-testid="wizard-emergency-selector">
                  <button type="button" data-testid="wizard-emergency-no"
                    onClick={() => set("is_emergency", false)}
                    className={`border px-4 py-4 text-sm font-medium transition-colors ${
                      f.is_emergency === false ? "border-navy bg-slate-50 text-navy ring-1 ring-navy" : "border-border text-slate-600 hover:border-slate-300"
                    }`}>
                    Non-Emergency
                  </button>
                  <button type="button" data-testid="wizard-emergency-yes"
                    onClick={() => set("is_emergency", true)}
                    className={`border px-4 py-4 text-sm font-bold transition-colors ${
                      f.is_emergency === true ? "border-red-600 bg-red-50 text-red-600 ring-1 ring-red-600" : "border-border text-slate-600 hover:border-slate-300"
                    }`}>
                    Emergency — urgent response
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-heading font-black tracking-tight text-navy text-2xl">Where is the project?</h2>
              <div>
                <label className="text-sm font-medium text-navy block mb-2">City</label>
                <select data-testid="wizard-city-select" value={f.city} onChange={(e) => set("city", e.target.value)} className={inputCls}>
                  <option value="">Select your city</option>
                  {locations.map((l) => <option key={l.id} value={l.city}>{l.city}, AL</option>)}
                  <option value="Other">Other (AL/TN)</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-2">ZIP Code</label>
                <input data-testid="wizard-zip-input" value={f.zip_code} inputMode="numeric" placeholder="35801"
                  onChange={(e) => set("zip_code", e.target.value.replace(/\D/g, "").slice(0, 5))} className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-2">Street address (optional)</label>
                <input data-testid="wizard-address-input" value={f.address} placeholder="123 Main St"
                  onChange={(e) => set("address", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-2">Describe your project (min. 10 characters)</label>
                <textarea data-testid="wizard-description-input" value={f.description} rows={4}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Tell the pros what you need done…"
                  className="w-full border border-border px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flame focus:border-transparent" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-8">
              <div>
                <h2 className="font-heading font-black tracking-tight text-navy text-2xl mb-2">Photos (optional)</h2>
                <p className="text-sm text-slate-500 mb-4">Up to 5 images help pros give better estimates.</p>
                <label className="flex items-center justify-center gap-3 border-2 border-dashed border-border hover:border-flame py-8 cursor-pointer transition-colors text-sm text-slate-500" data-testid="wizard-photo-upload">
                  <Upload size={18} className="text-flame" />
                  {uploading ? "Uploading…" : "Click to upload photos"}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} disabled={uploading || photos.length >= 5} />
                </label>
                {photos.length > 0 && (
                  <ul className="mt-3 space-y-2" data-testid="wizard-photo-list">
                    {photos.map((p) => (
                      <li key={p.id} className="flex items-center justify-between border border-border px-4 py-2 text-sm text-slate-600">
                        <span className="truncate">{p.name}</span>
                        <button type="button" onClick={() => setPhotos((ph) => ph.filter((x) => x.id !== p.id))} className="text-slate-400 hover:text-red-500">
                          <X size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h2 className="font-heading font-black tracking-tight text-navy text-2xl mb-4">Estimated budget</h2>
                <Radio options={BUDGETS} value={f.budget_range} onChange={(v) => set("budget_range", v)} testPrefix="wizard-budget" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-8">
              <div>
                <h2 className="font-heading font-black tracking-tight text-navy text-2xl mb-4">How would you like your estimate?</h2>
                <Radio
                  options={[
                    { value: "phone", label: "Phone estimate" },
                    { value: "email", label: "Email estimate" },
                    { value: "onsite", label: "On-site estimate (in-person visit)" },
                  ]}
                  value={f.estimate_method} onChange={(v) => set("estimate_method", v)} testPrefix="wizard-estimate" />
              </div>
              {f.estimate_method === "onsite" && (
                <div data-testid="wizard-appointment-calendar">
                  <h3 className="font-heading font-bold text-navy text-lg mb-1">
                    Preferred appointment date{f.is_emergency ? " (optional)" : ""}
                  </h3>
                  <p className="text-xs text-slate-500 mb-3">
                    {f.is_emergency
                      ? "Emergency requests are sent to matching pros immediately — no appointment is required to submit."
                      : "This is a preferred time — the professional will confirm or suggest another time."}
                  </p>
                  <div className="border border-border inline-block bg-white">
                    <Calendar mode="single" selected={f.preferred_date} onSelect={(d) => set("preferred_date", d)}
                      disabled={{ before: new Date() }} />
                  </div>
                </div>
              )}
              {f.estimate_method && f.estimate_method !== "onsite" && (
                <div>
                  <label className="text-sm font-medium text-navy block mb-2">Preferred date (optional)</label>
                  <input data-testid="wizard-date-input" type="date" min={new Date().toISOString().split("T")[0]}
                    value={f.preferred_date ? format(f.preferred_date, "yyyy-MM-dd") : ""}
                    onChange={(e) => set("preferred_date", e.target.value ? new Date(e.target.value + "T12:00:00") : null)}
                    className={inputCls} />
                </div>
              )}
              {f.estimate_method && (
                <div>
                  <label className="text-sm font-medium text-navy block mb-2">Preferred time window</label>
                  <select data-testid="wizard-time-select" value={f.preferred_time_window} onChange={(e) => set("preferred_time_window", e.target.value)} className={inputCls}>
                    <option value="">Select a time window</option>
                    {TIME_WINDOWS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <h2 className="font-heading font-black tracking-tight text-navy text-2xl">How should pros contact you?</h2>
              <Radio
                options={[
                  { value: "phone", label: "Phone call" },
                  { value: "text", label: "Text message" },
                  { value: "email", label: "Email" },
                ]}
                value={f.contact_preference} onChange={(v) => set("contact_preference", v)} testPrefix="wizard-contact-pref" />
              <div>
                <label className="text-sm font-medium text-navy block mb-2">Full name</label>
                <input data-testid="wizard-contact-name" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-2">Phone number</label>
                <input data-testid="wizard-contact-phone" value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="(256) 555-0100" className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-2">Email</label>
                <input data-testid="wizard-contact-email" type="email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} />
              </div>
            </div>
          )}

          {step === 5 && (
            <div data-testid="wizard-review">
              <h2 className="font-heading font-black tracking-tight text-navy text-2xl mb-6">Review your request</h2>
              <dl className="divide-y divide-border text-sm">
                {[
                  ["Service", categoryName],
                  ["Urgency", f.is_emergency ? "URGENT / EMERGENCY" : "Standard"],
                  ["Location", `${f.city}, ${f.zip_code}`],
                  ["Address", f.address || "—"],
                  ["Description", f.description],
                  ["Photos", photos.length ? `${photos.length} attached` : "None"],
                  ["Budget", f.budget_range],
                  ["Estimate method", { phone: "Phone estimate", email: "Email estimate", onsite: "On-site estimate" }[f.estimate_method]],
                  ["Preferred date", f.preferred_date ? format(f.preferred_date, "MMMM d, yyyy") : "Flexible"],
                  ["Time window", f.preferred_time_window || "Flexible"],
                  ["Contact via", f.contact_preference],
                  ["Contact", `${f.contact_name} · ${f.contact_phone} · ${f.contact_email}`],
                ].map(([k, v]) => (
                  <div key={k} className="py-3 grid grid-cols-3 gap-4">
                    <dt className="text-slate-400 font-medium">{k}</dt>
                    <dd className={`col-span-2 ${k === "Urgency" && f.is_emergency ? "text-red-600 font-bold" : "text-navy"}`}>{v}</dd>
                  </div>
                ))}
              </dl>
              {f.estimate_method === "onsite" && (
                <p className="text-xs text-slate-500 mt-4 bg-[#F4F5F7] border border-border px-4 py-3">
                  Your appointment is a <strong>preferred time</strong>, not confirmed. The professional can accept it or suggest another time.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-6">
          <button data-testid="wizard-back-btn" onClick={() => (step === 0 ? navigate(-1) : setStep(step - 1))}
            className="flex items-center gap-2 text-slate-500 hover:text-navy text-sm font-medium transition-colors">
            <ArrowLeft size={16} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button data-testid="wizard-next-btn" disabled={!canNext} onClick={() => setStep(step + 1)}
              className="flex items-center gap-2 bg-navy hover:bg-navy-light disabled:opacity-40 text-white font-heading font-bold uppercase tracking-wider text-sm px-8 py-4 transition-colors">
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button data-testid="wizard-submit-btn" disabled={submitting} onClick={submit}
              className="bg-flame hover:bg-flame-dark disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wider text-sm px-8 py-4 transition-colors">
              {submitting ? "Submitting…" : "Submit Request"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
